import React, { useState } from 'react';
import { X, ChevronDown, ChevronUp } from 'lucide-react';
import { detailedGallery } from '../detailedGalleryData';

export const GallerySection = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedDomain, setSelectedDomain] = useState('tous');
  const [expandedSubcategory, setExpandedSubcategory] = useState(null);

  // Listen for filter changes from Services section
  React.useEffect(() => {
    const handleFilterGallery = (event) => {
      const { domain } = event.detail;
      setSelectedDomain(domain);
    };

    window.addEventListener('filterGallery', handleFilterGallery);
    return () => window.removeEventListener('filterGallery', handleFilterGallery);
  }, []);

  const domains = [
    { id: 'tous', label: 'Tous' },
    { id: 'batiment', label: 'Bâtiment' },
    { id: 'peinture', label: 'Peinture' },
    { id: 'ouvragesTechniques', label: 'Ouvrages Techniques' },
    { id: 'evenementiel', label: 'Événementiel' },
    { id: 'securite', label: 'Mise en Sécurité' },
    { id: 'nettoyage', label: 'Nettoyage' },
    { id: 'toiture', label: 'Toiture & Couverture' },
    { id: 'descentes', label: 'Descente Pluviale & Fumisterie' },
    { id: 'maintenance', label: 'Entretien & Maintenance' }
  ];

  // Get all images or filter by domain
  const getFilteredImages = () => {
    if (selectedDomain === 'tous') {
      const allImages = [];
      Object.entries(detailedGallery).forEach(([domainKey, domain]) => {
        Object.entries(domain.subcategories).forEach(([subKey, subcategory]) => {
          subcategory.images.forEach(img => {
            allImages.push({
              ...img,
              domain: domain.title,
              subcategory: subcategory.title,
              domainKey,
              subKey
            });
          });
        });
      });
      return allImages;
    } else {
      const domain = detailedGallery[selectedDomain];
      if (!domain) return [];
      
      const images = [];
      Object.entries(domain.subcategories).forEach(([subKey, subcategory]) => {
        subcategory.images.forEach(img => {
          images.push({
            ...img,
            domain: domain.title,
            subcategory: subcategory.title,
            domainKey: selectedDomain,
            subKey
          });
        });
      });
      return images;
    }
  };

  const filteredImages = getFilteredImages();
  const selectedDomainData = selectedDomain !== 'tous' ? detailedGallery[selectedDomain] : null;

  return (
    <section id="galerie" className="py-20 bg-gray-50">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-black mb-4">
            Notre <span className="text-orange-600">Galerie</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
            Découvrez nos réalisations par domaine d'intervention
          </p>
        </div>

        {/* Domain Filter - Sticky */}
        <div className="sticky top-20 z-40 bg-gray-50 pb-6 -mx-4 px-4">
          <div className="flex flex-wrap justify-center gap-3 bg-white rounded-2xl shadow-lg p-4">
            {domains.map((domain) => (
              <button
                key={domain.id}
                onClick={() => setSelectedDomain(domain.id)}
                className={`px-6 py-2 rounded-full font-medium transition-all ${
                  selectedDomain === domain.id
                    ? 'bg-orange-600 text-white shadow-lg'
                    : 'bg-gray-100 text-gray-700 hover:bg-orange-50 hover:text-orange-600'
                }`}
              >
                {domain.label}
              </button>
            ))}
          </div>
        </div>

        {/* Subcategories - Photos uniquement */}
        {selectedDomainData && (
          <div className="max-w-5xl mx-auto mb-8">
            <div className="grid md:grid-cols-2 gap-4">
              {Object.entries(selectedDomainData.subcategories).map(([key, subcategory]) => (
                <div key={key} className="bg-white rounded-lg shadow-md overflow-hidden">
                  <button
                    onClick={() => setExpandedSubcategory(expandedSubcategory === key ? null : key)}
                    className="w-full p-4 flex items-center justify-between hover:bg-orange-50 transition-colors"
                  >
                    <div className="text-left">
                      <h4 className="font-bold text-black">{subcategory.title}</h4>
                      <p className="text-sm text-gray-600 mt-1">{subcategory.images.length} photos</p>
                    </div>
                    {expandedSubcategory === key ? (
                      <ChevronUp className="text-orange-600" />
                    ) : (
                      <ChevronDown className="text-orange-600" />
                    )}
                  </button>
                  {expandedSubcategory === key && (
                    <div className="p-4 bg-gray-50 border-t">
                      <p className="text-gray-700 text-sm">{subcategory.description}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Gallery Grid */}
        {filteredImages.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredImages.map((image) => {
              const isContain = image.fit === 'contain';
              return (
                <div
                  key={image.id}
                  onClick={() => setSelectedImage(image)}
                  className="group relative overflow-hidden rounded-xl cursor-pointer shadow-lg hover:shadow-2xl transition-all duration-300 bg-gray-100"
                >
                  <img
                    src={image.url}
                    alt={image.title}
                    className={`w-full h-80 ${isContain ? 'object-contain' : 'object-cover'} transition-transform duration-300 group-hover:scale-110`}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent opacity-100 transition-opacity duration-300 pointer-events-none">
                    {(image.title || image.subcategory) && (
                      <div className="absolute bottom-0 left-0 right-0 p-6">
                        {image.title && (
                          <p className="text-white font-semibold text-lg mb-1">{image.title}</p>
                        )}
                        <p className="text-orange-400 text-sm">{image.subcategory}</p>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="bg-white rounded-xl p-8 max-w-md mx-auto shadow-md">
              <h3 className="text-xl font-bold text-black mb-2">
                Aucune photo dans cette catégorie
              </h3>
              <p className="text-gray-600">
                Sélectionnez un autre domaine d'intervention
              </p>
            </div>
          </div>
        )}

        {/* Lightbox Modal */}
        {selectedImage && (
          <div
            className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4"
            onClick={() => setSelectedImage(null)}
          >
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute top-4 right-4 text-white hover:text-orange-600 transition-colors"
            >
              <X size={32} />
            </button>
            <div className="max-w-5xl w-full">
              <img
                src={selectedImage.url}
                alt={selectedImage.title}
                className="w-full h-auto rounded-lg"
                onClick={(e) => e.stopPropagation()}
              />
              <div className="text-center mt-6">
                <h3 className="text-white text-2xl font-bold mb-2">{selectedImage.title}</h3>
                <p className="text-orange-400 text-lg">{selectedImage.subcategory} • {selectedImage.domain}</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

